// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import * as Location from 'expo-location';
import { Platform } from 'react-native';
import { useEffect, useState } from 'react';

/**
 * Which way the phone is pointing, while the hole view is open.
 *
 * Separate from {@link usePlayerPosition} because it answers a different question — that one
 * is where you are, this is which way you are facing — and because the compass is the less
 * trustworthy of the two. A magnetometer near a trolley, a set of clubs or a cart is simply
 * wrong, so anything drawn from this should read as a hint rather than an instrument.
 *
 * Native only: expo-location ships no web implementation of `watchHeadingAsync`.
 */

/** Below this calibration level the reading is not worth drawing. */
const MIN_ACCURACY = 1;

export type DeviceHeadingState = {
  /** Degrees clockwise from north, or null when there is nothing trustworthy to show. */
  heading: number | null;
};

export const useDeviceHeading = ({ enabled }: { enabled: boolean }): DeviceHeadingState => {
  const [heading, setHeading] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled || Platform.OS === 'web') {
      setHeading(null);
      return undefined;
    }

    let cancelled = false;
    let subscription: Location.LocationSubscription | null = null;

    const begin = async () => {
      try {
        subscription = await Location.watchHeadingAsync((reading) => {
          if (cancelled) return;
          // An uncalibrated compass points confidently in the wrong direction, which is worse
          // than pointing nowhere.
          if (reading.accuracy < MIN_ACCURACY) {
            setHeading(null);
            return;
          }
          // True north needs the location permission and comes back as -1 without it;
          // magnetic north is close enough to aim a golf shot by.
          const trueHeading = reading.trueHeading;
          setHeading(trueHeading >= 0 ? trueHeading : reading.magHeading);
        });
      } catch {
        // No compass, or a platform that refuses. The view works without it.
        if (!cancelled) setHeading(null);
      }
    };

    void begin();

    return () => {
      cancelled = true;
      // Same guard as the position watch: expo-location's teardown can throw, and unmounting
      // must not take the screen down with it.
      try {
        subscription?.remove();
      } catch {
        // ignore
      }
      subscription = null;
    };
  }, [enabled]);

  return { heading };
};
